/**
 * One conversation, two channels.
 *
 * A customer can be talking to Roof Board's Twilio line and to a rep's own
 * mobile at the same time, and from the customer's side that is not two
 * conversations — it is one, and they will not remember which number they used.
 * So the screen shows one thread, ordered by time, with each message saying
 * which way it went out.
 *
 * The alternative — a Twilio section and a Personal section — was rejected for
 * the reason two sections are usually rejected: the answer to "what did we last
 * tell them" would be in whichever section somebody did not read.
 *
 * Everything here is pure. The queries filter `deleted_at is null` and the
 * database's policy filters it again; nothing in this file knows about deletion
 * because nothing here should ever see a deleted text.
 */

/** A Twilio message, as far as the merge cares. */
export interface RoofBoardText {
  message_sid: string;
  direction: string;
  sent_at: string;
}

/** A text synced from a rep's own phone, as far as the merge cares. */
export interface PersonalText {
  id: string;
  direction: string;
  sent_at: string;
  /** Whose phone it passed through. Null on rows stored before 2026-09-20. */
  source_profile_id?: string | null;
}

/** Which number a message went out on, or came in to. */
export type TextChannel = "roof-board" | "personal";

export type ThreadEntry<M extends RoofBoardText, P extends PersonalText> =
  | {
      /** Stable across renders and unique across both tables. */
      key: string;
      channel: "roof-board";
      sent_at: string;
      /** True when we sent it, whichever number it left from. */
      mine: boolean;
      message: M;
    }
  | {
      key: string;
      channel: "personal";
      sent_at: string;
      mine: boolean;
      text: P;
    };

/** True for a message this company sent, in either table's vocabulary. */
function isOurs(direction: string): boolean {
  return direction === "outbound" || direction === "outgoing";
}

/**
 * The two tables as one thread, oldest first.
 *
 * A message with a timestamp that will not parse is dropped rather than sorted
 * to one end: `Date.parse` gives NaN, every comparison against NaN is false,
 * and one such row leaves the whole conversation in an arbitrary order. Losing
 * one message that has no time is better than scrambling the ones that do.
 *
 * Ties are broken by channel and then by key, so two messages recorded in the
 * same second do not swap places between renders.
 */
export function mergeThread<M extends RoofBoardText, P extends PersonalText>(
  messages: readonly M[],
  personal: readonly P[],
): ThreadEntry<M, P>[] {
  const entries: (ThreadEntry<M, P> & { at: number })[] = [];

  for (const message of messages) {
    const at = Date.parse(message.sent_at);
    if (Number.isNaN(at)) continue;
    entries.push({
      at,
      key: `roof-board:${message.message_sid}`,
      channel: "roof-board",
      sent_at: message.sent_at,
      mine: isOurs(message.direction),
      message,
    });
  }

  for (const text of personal) {
    const at = Date.parse(text.sent_at);
    if (Number.isNaN(at)) continue;
    entries.push({
      at,
      key: `personal:${text.id}`,
      channel: "personal",
      sent_at: text.sent_at,
      mine: isOurs(text.direction),
      text,
    });
  }

  entries.sort(
    (a, b) =>
      a.at - b.at ||
      a.channel.localeCompare(b.channel) ||
      a.key.localeCompare(b.key),
  );

  return entries.map(({ at: _at, ...entry }) => entry as ThreadEntry<M, P>);
}

/* ------------------------------------------------------------- sending -- */

/** Which number a reply leaves from. */
export type TextFrom = "roof-board" | "cell";

/**
 * What the From picker says about each choice.
 *
 * The second sentence of each is the one that matters, and it is the same fact
 * twice: a customer replies to the number they were texted from. A rep who
 * sends from their own mobile has taken that conversation onto their phone, and
 * one who sends from the Roof Board line has left it where anybody can answer
 * it.
 */
export const TEXT_FROM_LABEL: Record<TextFrom, string> = {
  "roof-board": "Roof Board number",
  cell: "My cell",
};

export const TEXT_FROM_NOTE: Record<TextFrom, string> = {
  "roof-board":
    "Sent by Roof Board. Their reply comes back here, where anybody can answer it.",
  cell: "Opens Messages on your phone with the text ready — you tap send. Their reply goes to your phone, and both show up here within a minute.",
};

/* ----------------------------------------------------- whose phone it was -- */

/**
 * Whose phone a synced text came through.
 *
 * Said on every personal bubble since there were two Macs (ADR 0178). "Personal"
 * on its own was enough while one phone could produce one, and stopped being
 * enough the moment a second could: an outbound text reads as something this
 * company said, and the rep about to follow it up needs to know which colleague
 * to ask before they say something different.
 *
 * A text the customer sent is the other half of that, and it read wrongly until
 * 2026-09-23: an incoming message said "My phone", because the handset it
 * arrived on was the reader's own. Nobody reads a bubble asking which handset
 * carried it — they read it asking who said it, and the answer on an inbound
 * text is the customer. So an incoming text is named after them, and only an
 * outgoing one is named after the phone it left from.
 *
 * A first name either way, because the thread is internal and a bubble is
 * narrow. The possessive follows the ordinary rule — a name already ending in s
 * takes the apostrophe alone — which matters for exactly the reason small things
 * do: a screen that writes "Lysa's" and "Chris's" in the same list looks like
 * two people wrote it.
 *
 * No name at all is "a rep's phone" or "their phone" rather than a blank or a
 * guess. For the rep that is a row from before the column existed, and the
 * honest answer to which phone is that nobody wrote it down.
 */
export function personalPhoneLabel({
  outbound,
  ownerName,
  ownPhone = false,
  contactName,
}: {
  /** True when this company sent it, false when the customer did. */
  outbound: boolean;
  /** Whose handset it passed through. Null on rows stored before 2026-09-20. */
  ownerName?: string | null;
  /** True when that handset belongs to the person reading the screen. */
  ownPhone?: boolean;
  /** The customer at the other end of the thread. */
  contactName?: string | null | undefined;
}): string {
  if (!outbound) {
    const first = firstName(contactName);
    return first === "" ? "Their phone" : `${possessive(first)} phone`;
  }

  if (ownPhone) return "My phone";

  const first = firstName(ownerName);
  if (first === "") return "A rep's phone";

  return `${possessive(first)} phone`;
}

const firstName = (name: string | null | undefined): string =>
  (name ?? "").trim().split(/\s+/)[0] ?? "";

const possessive = (first: string): string =>
  `${first}${first.endsWith("s") ? "'" : "'s"}`;

/**
 * May this person take that text down?
 *
 * The same sentence the database enforces in `delete_personal_text`, kept here
 * so the button is not offered where the answer is no: your own phone's texts,
 * and only if your phone syncs at all. Everybody in the office reads both
 * people's — that is ADR 0090 and this does not revisit it — but a text from
 * somebody else's handset is not yours to remove.
 *
 * A text with no source is nobody's, so anybody with the marker may remove it.
 * That is the deliberate opposite of the usual "deny when unsure": the
 * alternative is a message about a customer that no screen in the company can
 * ever take down.
 */
export function mayDeletePersonalText(
  text: Pick<PersonalText, "source_profile_id">,
  viewer: { id?: string | null; syncs_personal_texts?: boolean | null } | null,
): boolean {
  if (viewer?.syncs_personal_texts !== true) return false;
  const source = text.source_profile_id ?? null;
  return source === null || source === viewer.id;
}
